import fullStoryPlugin from '@analytics/fullstory';
import { Analytics, AnalyticsInstance } from 'analytics';
// eslint-disable-next-line import/no-named-as-default
import mParticlePlugin from './analyticsMParticle';
import { Theme } from '@cmctechnology/webinvest-constants';

// eslint-disable-next-line import/no-mutable-exports
let analytics: AnalyticsInstance;
const FULLSTORY_PARAM = '_fs';
export interface PageView {
  Path: string;
  Url: string;
  Host: string;
  Title?: string;
  Search?: string;
}

export interface UserAttributes {
  Theme?: Theme;
  ActivityStatus?: ActivityStatus;
  Persona?: Persona;
  PremiumDesk?: boolean;
  PremiumDeskManager?: string;
  Tenure?: number;
  Falcon?: boolean;
  displayName?: string; // Fullstory
}

export interface EventAttributes {
  AccountId?: string;
}

//mParticle.EventType
export enum EventType {
  Unknown = 0,
  Navigation = 1,
  Location = 2,
  Search = 3,
  Transaction = 4,
  UserContent = 5,
  UserPreference = 6,
  Social = 7,
  Other = 8,
  Media = 9
}
export interface EventOptions {
  type: EventType;
}

export enum ActivityStatus {
  Active = 'Active',
  Dormant = 'Dormant',
  Inactive = 'Inactive',
  NeverTraded = 'NeverTraded'
}

export enum Persona {
  Alexander = 'Alexander',
  Finn = 'Finn',
  Lachy = 'Lachy',
  Tyler = 'Tyler',
  Unknown = 'Unknown',
  William = 'William'
}
export interface IAnalyticsConfig {
  debug: boolean;
  fullStory: {
    isEnabled: boolean;
    organisationId: string;
  };
  braze: {
    isEnabled: boolean;
    apiKey: string;
    baseUrl: string;
    contentSecurityNonce: string;
  };
  mParticle: {
    isEnabled: boolean;
    apiKey: string;
    isDevelopmentMode: boolean;
    workspaceToken: string;
    deviceId?: string;
    logLevel?: 'verbose' | 'warning' | 'none' | undefined;
    dataPlan: {
      planId: string;
      planVersion: number;
    };
    customFlags: any;
    enabledPlugins: {
      googleTagManager: boolean;
      braze: boolean;
      googleAnalytics4: boolean;
      bing: boolean;
      twitter: boolean;
      facebook: boolean;
      oneTrust: boolean;
      googleAds: boolean;
    };
  };
}

/**
 * To allow cross-domain sessions in fullStory, this reads the fullStory
 * cookie from the URL and sets it in localStorage before starting fullStory
 */
export const setFullStorySession = () => {
  const params = new URL(window.location.href).searchParams;
  const fsParam = params.get(FULLSTORY_PARAM);
  if (fsParam) {
    const fsSessionId = decodeURIComponent(fsParam);
    localStorage.setItem('_fs_uid', fsSessionId);
  }
};

export const init = async (config: IAnalyticsConfig): Promise<AnalyticsInstance> => {
  setFullStorySession();
  const plugins = [];
  if (config.fullStory.isEnabled) {
    plugins.push(
      fullStoryPlugin({
        org: config.fullStory.organisationId
      })
    );
  }

  if (config.mParticle.isEnabled) {
    plugins.push(
      mParticlePlugin(
        config.mParticle.apiKey,
        {
          ...config.mParticle
        },
        config.mParticle.enabledPlugins
      )
    );
  }

  analytics = Analytics({
    app: `web`,
    version: `1.0`,
    debug: config.debug,
    plugins
  });

  return new Promise((resolve) => {
    analytics.ready(async () => {
      try {
        resolve(analytics);
      } catch {
        resolve(analytics);
      }
    });
  });
};

export const identifyUser = async (crc: string, userAttributes: UserAttributes) => {
  crc = crc.toString();
  return analytics.identify(crc, userAttributes);
};

export const logout = async () => {
  return analytics.reset();
};

export const pageView = async (page: PageView, attributes?: EventAttributes, requireLoggedIn?: boolean) => {
  return analytics.page({ ...page }, { attributes, requireLoggedIn });
};

export const trackEvent = async (eventName: string, payload?: EventAttributes, options?: EventOptions) => {
  return analytics.track(eventName, payload, options);
};

export const setUserAttribute = async (attribute: string, value: string) => {
  //  @ts-ignore
  return analytics.plugins.mParticle.setUserAttribute(attribute, value);
};
